import { Star } from "lucide-react";

import {
  dayKey,
  formatClockTime,
  formatFullTimestamp,
  formatMonthDay,
} from "../../../../components/audit/auditLog/auditDates";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { cn } from "../../../../lib/utils";
import { type StepCompletion } from "../lib/deterministicSteps";
import { CascadeOperationGlyph } from "./CascadeOperationGlyph";

const DID_NOT_FINISH = "Did not finish";
const NO_COMPLETION_TIME = "No completion time";

/**
 * When the step finished, or what its missing timestamp means.
 *
 * A step with no completion is stated rather than blanked: an empty cell reads
 * as a step that ran and produced nothing, which is a different run from one
 * that stopped part-way through. Which of those a null means is settled before
 * the row is built — the caller hands over the reading, and this renders it — so
 * the steps tab cannot call a step still running while the review pane calls the
 * same step the one the run stopped at.
 *
 * A finished step reads to the minute, without seconds. The row sits in a column
 * narrow enough that every character it spends is one the step's name loses, and
 * the second a step finished on settles nothing an auditor reading a list of
 * steps is asking. The unparseable timestamp is shown as it arrived rather than
 * reformatted into something the wire did not say.
 *
 * The date is the run's, stated once per surface — on the steps tab's heading
 * and the review pane's WORKFLOW line — so a row carries one only where its
 * step finished on a different day from the run; a run that crosses midnight
 * would otherwise read as finishing before it started.
 */
function completionLabel(
  completion: StepCompletion,
  runDate: Date | null,
): string {
  switch (completion.kind) {
    case "finished": {
      const finished = new Date(completion.completedAt);
      if (Number.isNaN(finished.getTime())) return completion.completedAt;
      const time = formatClockTime(finished).toLowerCase();
      return runDate !== null && dayKey(finished) !== dayKey(runDate)
        ? `${formatMonthDay(finished)} ${time}`
        : time;
    }
    case "unfinished":
      return DID_NOT_FINISH;
    case "unrecorded":
      return NO_COMPLETION_TIME;
  }
}

/** The full stamp behind a finished step's shortened time, where it parses. */
function completionStamp(completion: StepCompletion): string | undefined {
  if (completion.kind !== "finished") return undefined;
  const finished = new Date(completion.completedAt);
  return Number.isNaN(finished.getTime())
    ? undefined
    : formatFullTimestamp(finished);
}

interface AnalyticsStepRowProps {
  stepName: string;
  /**
   * Cascade's id for what the step did, where the caller holds it. Only the
   * glyph is chosen by it, and an unrecognised or absent operation costs the row
   * nothing but a distinguishing picture.
   */
  operation?: string | null;
  /** Already read against the run's terminality by the caller. */
  completion: StepCompletion;
  /** The run's date, per `verdictRunDate`, which the time is read against. */
  runDate: Date | null;
  /**
   * Whether the step is included in the workpaper, or null where it has nothing
   * to star — a step the run cited no card for carries no include flag, and an
   * unfilled star over it would offer a write with nowhere to land.
   */
  starred: boolean | null;
  /** The row whose card the viewer beside it is showing. */
  active: boolean;
  /**
   * Absent on a surface with no viewer pane to point at. The row is then drawn
   * as the same row without being a control: a button that selects a step
   * nothing can display is an invitation to a preview that never appears.
   */
  onSelect?: () => void;
  /** Absent where the host cannot write the flag; the star is then read-only. */
  onToggleStar?: () => void;
}

/**
 * One operation of an analytics run: a glyph, a name, a completion time and a
 * star, on a card of its own.
 *
 * **The one drawing of a step, rendered by every surface that lists one.** The
 * Deterministic Steps tab lists the run's operations and the review pane's
 * key-steps section lists the starred ones, and the two are the same rows over
 * different subsets — a second copy of this markup is two drawings of one step
 * free to drift until an auditor reading them side by side sees two runs.
 *
 * **It restates nothing a card says.** The rule the step applied, the counts it
 * moved and the sentence the engine wrote about it are on the card the server
 * rendered for that step, and selecting the row opens it. Drawing any of that
 * here as well is two accounts of one step waiting to disagree.
 *
 * **It is drawn for the narrow column, not the wide one.** The review pane holds
 * it in roughly 280 points, so the glyph, the time and the star hold their width
 * and the name absorbs every truncation — a row that reserved space for a full
 * step name would push the star out of the pane on the surface that uses it
 * most.
 *
 * It takes primitives rather than a row or a card, because its two callers hold
 * different records of the same step and neither's shape is the thing being
 * drawn.
 */
export function AnalyticsStepRow({
  stepName,
  operation,
  completion,
  runDate,
  starred,
  active,
  onSelect,
  onToggleStar,
}: AnalyticsStepRowProps) {
  const body = (
    <>
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors",
          active ? "text-stone-700" : "bg-stone-100 text-stone-500",
        )}
      >
        <CascadeOperationGlyph operation={operation} className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {stepName}
      </span>
    </>
  );

  const bodyClass = "flex min-w-0 flex-1 items-center gap-2.5 text-left";

  const label = completionLabel(completion, runDate);
  const stamp = completionStamp(completion);
  const timeClass = cn(
    "ml-1 shrink-0 text-xs text-stone-400",
    completion.kind === "finished" && "tabular-nums",
  );
  const time =
    stamp === undefined ? (
      <span className={timeClass}>{label}</span>
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn(timeClass, "rounded-sm")}>
            <span aria-hidden>{label}</span>
            <span className="sr-only">{stamp}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{stamp}</TooltipContent>
      </Tooltip>
    );

  return (
    /* Its own card rather than a row in a shared box: the step is the unit an
       auditor selects, stars and compares across the two tabs, and a list that
       welds them together makes the boundary between two steps the faintest
       line on screen. */
    <li
      className={cn(
        "flex items-center gap-1.5 rounded-lg border px-2 py-1.5 transition-colors",
        active
          ? "border-blue-300 bg-blue-50 text-stone-900 ring-1 ring-blue-100"
          : "border-stone-200 bg-white text-stone-700",
        onSelect && !active && "hover:border-stone-300 hover:bg-stone-50",
      )}
    >
      {onSelect ? (
        <button type="button" onClick={onSelect} className={bodyClass}>
          {body}
        </button>
      ) : (
        <div className={bodyClass}>{body}</div>
      )}

      {/* Beside the select button rather than inside it: the time is a
          focusable tooltip trigger, and a focusable element nested in a button
          is invalid interactive content. */}
      {time}

      {/* The citation's own include flag, which is the whole of the selection
          the workpaper exports — so one press here moves the step into or out
          of the review pane's key-steps section. */}
      {starred !== null &&
        (onToggleStar ? (
          <button
            type="button"
            onClick={onToggleStar}
            title={starred ? "Remove from workpaper" : "Include in workpaper"}
            aria-pressed={starred}
            className="shrink-0 rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
          >
            <Star
              className={cn(
                "h-4 w-4",
                starred && "fill-amber-400 text-amber-400",
              )}
            />
          </button>
        ) : (
          <span className="shrink-0 p-1 text-stone-400">
            <Star
              aria-hidden
              className={cn(
                "h-4 w-4",
                starred && "fill-amber-400 text-amber-400",
              )}
            />
            <span className="sr-only">
              {starred
                ? "Included in the workpaper"
                : "Not included in the workpaper"}
            </span>
          </span>
        ))}
    </li>
  );
}
